import React from "react";
import {
  Facebook,
  Twitter,
  Youtube,
  Instagram,
  Mail,
  Phone,
  MapPin,
  Clock,
  ChevronRight,
  PhoneCall,
  Linkedin,
} from "lucide-react";
import { FcGoogle } from "react-icons/fc";
import { BsWhatsapp } from "react-icons/bs";

import logo from "../assets/images/logo/logo.png";

const COLORS = {
  cream: "#FAF7F2", // Premium Cream Base
  red: "#A10F1A", // Deep Crimson Red
  gold: "#C6A75E", // Luxury Gold Accent
  textDark: "#1A1A1A", // Charcoal for readable text on Cream
  textLight: "#FFFFFF", // Pure White for text on Red
};

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="w-full relative shadow-inner">
      {/* =========================================
          TOP SECTION: CREAM BACKGROUND
          ========================================= */}
      <div
        className="pt-20 pb-16 px-6 md:px-12 transition-colors duration-300"
        style={{ backgroundColor: COLORS.cream, color: COLORS.textDark }}
      >
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16">
          {/* 1. LOGO & MISSION */}
          <div className="space-y-6">
            <div className="flex flex-col">
              <img
                src={logo}
                alt="R & P Law Partners Logo"
                className="w-64 h-auto object-contain mb-4"
              />
              <div
                className="h-[3px] w-24 rounded-full"
                style={{ backgroundColor: COLORS.red }}
              ></div>
            </div>
            <p
              className="text-base leading-relaxed italic opacity-90 font-serif border-l-4 pl-5"
              style={{ borderColor: COLORS.gold }}
            >
              "Justice is the constant and perpetual will to allot to every man
              his due."
            </p>
          </div>

          {/* 2. QUICK LINKS */}
          <div>
            <h3
              className="text-xl font-bold mb-8 uppercase tracking-widest flex items-center gap-3"
              style={{ color: COLORS.red }}
            >
              <div
                className="w-2.5 h-2.5 rotate-45"
                style={{ backgroundColor: COLORS.gold }}
              ></div>
              Quick Links
            </h3>
            <ul className="space-y-4 text-base font-medium">
              <FooterLink label="Home" href="/" />
              <FooterLink label="About Us" href="/about-us" />
              <FooterLink label="Practice Areas" href="/services" />
              <FooterLink label="Latest Blogs" href="/blogs" />
              <FooterLink label="Contact Us" href="/contact-us" />
            </ul>
          </div>

          {/* 3. EXPERTISE */}
          <div>
            <h3
              className="text-xl font-bold mb-8 uppercase tracking-widest flex items-center gap-3"
              style={{ color: COLORS.red }}
            >
              <div
                className="w-2.5 h-2.5 rotate-45"
                style={{ backgroundColor: COLORS.gold }}
              ></div>
              Expertise
            </h3>
            <ul className="space-y-4 text-base font-medium">
              <FooterLink
                label="Criminal Defense"
                href="/services/criminal-cases"
              />
              <FooterLink
                label="Civil Litigation"
                href="/services/civil-cases"
              />
              <FooterLink
                label="Matrimonial Matters"
                href="/services/matrimonial-cases"
              />
              <FooterLink
                label="Immigration Law"
                href="/sservices/immigration-law"
              />
              <FooterLink
                label="Company Related Issue
"
                href="/services/company-related-issue"
              />
            </ul>
          </div>

          {/* 4. CHAMBERS & CONTACT */}
          <div className="space-y-8">
            <h3
              className="text-xl font-bold mb-8 uppercase tracking-widest flex items-center gap-3"
              style={{ color: COLORS.red }}
            >
              <div
                className="w-2.5 h-2.5 rotate-45"
                style={{ backgroundColor: COLORS.gold }}
              ></div>
              Contact Info
            </h3>
            <div className="space-y-6 text-base">
              <div className="flex items-start gap-4 group">
                <MapPin
                  size={22}
                  className="shrink-0 transition-transform group-hover:scale-110 mt-1"
                  style={{ color: COLORS.red }}
                />
                <span className="leading-snug font-medium opacity-90">
                  Chamber No.- 890, Patiala House Court, New Delhi- 110001
                  <br />
                </span>
              </div>
              <div className="flex items-start gap-4 group">
                <MapPin
                  size={22}
                  className="shrink-0 transition-transform group-hover:scale-110 mt-1"
                  style={{ color: COLORS.red }}
                />
                <span className="leading-snug font-medium opacity-90">
                  23/07, Wave One, Sector- 18, Noida, U. P.- 201301
                </span>
              </div>
              <div className="flex items-center gap-4 group">
                <Phone
                  size={20}
                  className="transition-transform group-hover:scale-110"
                  style={{ color: COLORS.red }}
                />
                <span className="font-bold text-lg">
                  +91-7042278441
                  <br />
                  +91-9661900233
                  <br />
                  +91-9953311115
                </span>
              </div>
              <div className="flex items-center gap-4 group">
                <Mail
                  size={20}
                  className="transition-transform group-hover:scale-110"
                  style={{ color: COLORS.red }}
                />
                <span className="font-medium hover:opacity-70 transition-opacity break-all cursor-pointer">
                  admin@rplawpartners.com <br /> ritika@rplawpartners.com
                </span>
              </div>
              <div className="flex items-center gap-4 pt-4 border-t border-black/10">
                <Clock size={20} style={{ color: COLORS.gold }} />
                <span className="text-xs font-bold uppercase tracking-widest opacity-70">
                  Mon – Sat: 09:00 AM – 10:00 PM
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =========================================
          BOTTOM SECTION: RED BACKGROUND
          ========================================= */}
      <div
        className="py-8 px-6 md:px-12"
        style={{ backgroundColor: COLORS.red, color: COLORS.textLight }}
      >
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          {/* Social Icons */}
          <div className="flex space-x-4">
            <SocialIcon
              icon={<FcGoogle size={18} />}
              href="https://www.google.com/search?q=R+%26+P+Law+Partners+%7C+Criminal+%7C+Bail+%7C+Domestic+Violence+%7C+NDPS+%7C+POCSO+%7C+Divorce+%7C+Cyber+Crime&stick=H4sIAAAAAAAA_-NgU1I1qDC2NEhONTZOSTGzNDU3MjW3MqgwMTNJMTYxNTAyTjW2SExOW8QaH6SgphCg4JNYrhCQWFSSl1pUrFCj4FyUmZuZl5gDZDolZoIol_zc1OKSzGSFsMz8nNS85FSgmJ9LQDCQCvB3DvYHKcksyy8CSzhXJqUWgQ1JBQC8BQpQiwAAAA&hl=en-GB&mat=CYHB-pCwiS_NElcBa0lj__bqd8ibkcgaUK0jibVns8PUAMBSSWBMo7nW5fvRfuGZp3YC4BEjjU7_Zf7ceky2EvCLaTDReT8ha7gG3XQXfe_5q6yWYZf4fCxinPppe6GsSKY&authuser=1"
            />

            <SocialIcon
              icon={<Instagram size={18} />}
              href="https://www.instagram.com/rplawpartners/?hl=en"
            />
            <SocialIcon
              icon={<Facebook size={18} />}
              href="https://www.facebook.com/people/R-P-Law/pfbid02RFedaYHea2QP2Hkf4DK83NhvzLFtBZgTQWpHpwYJTbc6QS5m3juPFwp1mHvTkFFql/"
            />
          </div>

          {/* Copyright Area */}
          <div className="text-center md:text-right space-y-2">
            <p className="text-xs font-bold tracking-[0.3em] uppercase opacity-90">
              © {currentYear} R & P Law Partners | All Rights Reserved
            </p>
            <p className="text-[10px] text-white/70 tracking-widest uppercase">
              Design and Developed by{" "}
              <a
                href="https://www.digimarqtechnology.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold hover:underline underline-offset-4 transition-all"
                style={{ color: COLORS.gold }}
              >
                Digimarq Technology Pvt Ltd
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Floating Contact Buttons */}
      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[9999] flex flex-col gap-3 sm:gap-4">
        {/* WhatsApp */}
        <a
          href="https://wa.me/919661900233"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="WhatsApp"
          className="
      w-12 h-12
      sm:w-14 sm:h-14
      rounded-full
      flex items-center justify-center
      shadow-xl
      hover:scale-110
      transition-all duration-300
      animate-pulse
    "
          style={{
            backgroundColor: "#25D366",
            color: "#fff",
          }}
        >
          <BsWhatsapp className="text-[22px] sm:text-[28px]" />
        </a>

        {/* Call */}
        <a
          href="tel:+919661900233"
          aria-label="Call Now"
          className="
      w-12 h-12
      sm:w-14 sm:h-14
      rounded-full
      flex items-center justify-center
      shadow-xl
      hover:scale-110
      transition-all duration-300
    "
          style={{
            backgroundColor: COLORS.red,
            color: "#fff",
          }}
        >
          <PhoneCall className="w-5 h-5 sm:w-7 sm:h-7" />
        </a>
      </div>
    </footer>
  );
};

/* --- Sub-components --- */
const SocialIcon = ({ icon, href }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center transition-all duration-300 hover:-translate-y-1"
    style={{ color: COLORS.textLight }}
    onMouseEnter={(e) => {
      e.currentTarget.style.backgroundColor = COLORS.cream;
      e.currentTarget.style.color = COLORS.red;
      e.currentTarget.style.borderColor = COLORS.cream;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.backgroundColor = "transparent";
      e.currentTarget.style.color = COLORS.textLight;
      e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.3)";
    }}
  >
    {icon}
  </a>
);

const FooterLink = ({ label, href }) => (
  <li className="flex items-center group cursor-pointer overflow-hidden">
    <ChevronRight
      size={16}
      style={{ color: COLORS.red }}
      className="mr-2 -translate-x-5 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-300"
    />
    <a
      href={href}
      className="opacity-80 group-hover:opacity-100 group-hover:translate-x-1 group-hover:font-semibold transition-all duration-300"
      style={{ color: COLORS.textDark }}
      onMouseEnter={(e) => (e.currentTarget.style.color = COLORS.red)}
      onMouseLeave={(e) => (e.currentTarget.style.color = COLORS.textDark)}
    >
      {label}
    </a>
  </li>
);

export default Footer;
